import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuX } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import downloadIcon from '@/assets/images/DownloadBlueIcon.svg'
import duplicateIcon from '@/assets/images/DuplicateBlue.svg'
import moveIcon from '@/assets/images/MoveBlue.svg'
import playIcon from '@/assets/images/StartTask.svg'
import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import Icon from '@/components/Icon'
import IconText from '@/components/IconText'
import { useAppSelector } from '@/hooks/redux'
import {
  useDeleteCaseImageMutation,
  useDuplicateCaseImageMutation,
} from '@/store/apiService'
import { clearImageSelection } from '@/store/project'
import { getProjectPermissions } from '@/utils/constants'
import { downloadImages } from '@/utils/helper'

import MoveImageDialog from './MoveImageDialog'
import StartTaskModal from './StartTaskModal'

interface MultipleImageActionsProps {
  caseId: string
  refetch: () => Promise<any>
  caseDetails: any
}

function MultipleImageActions({
  caseId,
  refetch,
  caseDetails,
}: MultipleImageActionsProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'ProjectDetails.ProjectDetailsActions',
  })

  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'ProjectDetails.DeleteActionDialog',
  })

  const dispatch = useDispatch()
  const selected = useAppSelector(
    (state) => state?.project?.selectedImageIds?.[caseId] || [],
  )
  const [duplicateCaseImage] = useDuplicateCaseImageMutation()
  const [deleteCaseImage, { isLoading: isDeletingImage }] =
    useDeleteCaseImageMutation()

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isMoveDialog, setIsMoveDialog] = useState(false)
  const [isStartTaskMenu, setIsStartTaskMenu] = useState(false)

  const permissions = getProjectPermissions(caseDetails?.role)

  if (!selected?.length) return null

  const duplicateCases = async () => {
    if (!caseId || !caseDetails?.images) return

    const originalImageCount = caseDetails.images.length
    const expectedNewCount = selected.length
    try {
      await duplicateCaseImage({
        ids: [selected],
        caseId: caseId,
      }).unwrap()

      toast.info(t('actionToast.duplicatingImage'))
      dispatch(clearImageSelection(caseId))

      const pollInterval = 2000
      const maxAttempts = 10
      let attempts = 0

      const pollForDuplication = async () => {
        try {
          const updated = await refetch()
          const updatedImages = updated?.data?.images || []

          if (updatedImages.length >= originalImageCount + expectedNewCount) {
            toast.success(t('actionToast.imageDuplicated'))
            return
          }

          if (++attempts < maxAttempts) {
            setTimeout(pollForDuplication, pollInterval)
          } else {
            toast.error(
              'Images may have duplicated, but update not detected in time.',
            )
          }
        } catch (err) {
          toast.error('Polling failed.')
        }
      }

      setTimeout(pollForDuplication, 2000)
    } catch (err) {
      toast.error(t('actionToast.imageDuplicateFailed'))
    }
  }

  const handleDownload = async () => {
    const selectedImages = caseDetails?.images?.filter((img: { id: number }) =>
      selected.includes(img.id),
    )

    if (!selectedImages?.length) return
    const formattedImages = selectedImages?.map(
      (img: {
        key_image: any
        type: string
        raw_image: any
        key_raw: { split: (arg0: string) => never[] }
        name: any
        image: any
      }) => {
        const names =
          img.type === 'raw' ? img.key_raw.split('/') : img.key_image.split('/')
        const fileName =
          names.length > 0 ? names?.[names.length - 1] : img?.name
        return {
          name: fileName,
          url: img.type === 'raw' ? img.raw_image : img.image,
        }
      },
    )

    try {
      await downloadImages(formattedImages)
      toast.success(t('actionToast.imageDownloaded'))
      dispatch(clearImageSelection(caseId))
    } catch (err) {
      toast.error(t('actionToast.imageDownloadFailed'))
    }
  }

  const selectedImagesDetails =
    caseDetails?.images?.filter((img: { id: number }) =>
      selected.includes(img.id),
    ) || []

  const selectedImageTypes = selectedImagesDetails.map(
    (img: { type: any }) => img.type,
  )

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: '#464646',
          borderRadius: 1,
          px: 2,
          py: '6px',
          width: '100%',
          flex: 1,
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography
            sx={{ color: 'white', fontSize: 14, fontWeight: 400, mr: 2 }}
          >
            {selected?.length} {t('select')}
          </Typography>

          {permissions?.canStartTask && (
            <IconText
              icon={playIcon}
              label={t('startTask').toUpperCase()}
              onClick={() => setIsStartTaskMenu(true)}
              iconStyle={{ height: 16, width: 16 }}
              spacing={0.5}
            />
          )}

          {permissions?.canDownload && (
            <IconText
              icon={downloadIcon}
              label={t('download').toUpperCase()}
              onClick={handleDownload}
              iconStyle={{ height: 16, width: 16 }}
              spacing={0.5}
            />
          )}

          {permissions?.canDuplicate && (
            <IconText
              icon={duplicateIcon}
              label={t('duplicate').toUpperCase()}
              onClick={duplicateCases}
              iconStyle={{ height: 16, width: 16 }}
              spacing={0.5}
            />
          )}

          {permissions?.canMoveImages && (
            <IconText
              icon={moveIcon}
              label={t('move').toUpperCase()}
              onClick={() => setIsMoveDialog(true)}
              iconStyle={{ height: 16, width: 16 }}
              spacing={0.5}
            />
          )}

          {permissions?.canDeleteImages && (
            <IconText
              icon={<DeleteIcon />}
              label={t('delete').toUpperCase()}
              onClick={() => setIsDeleteDialogOpen(true)}
              iconStyle={{
                height: 16,
                width: 16,
                color: 'rgba(144, 202, 249, 1)',
              }}
              spacing={0.5}
            />
          )}
        </Stack>

        <Icon
          icon={LuX}
          size={20}
          onClick={() => dispatch(clearImageSelection(caseId))}
        />
      </Box>

      <Dialog
        triggerElement={null}
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title={translate('title')}
        content={
          <p>
            {translate('content', { count: selected.length })} <br />
            {translate('action')}
          </p>
        }
        paperStyles={{ width: '600px' }}
        actionButtons={
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              label={translate('cancelButton')}
              onClick={() => setIsDeleteDialogOpen(false)}
              textTransform="uppercase"
            />
            <Button
              variant="contained"
              label={translate('deleteButton')}
              onClick={async () => {
                if (caseId) {
                  try {
                    await deleteCaseImage({
                      ids: [selected],
                      caseId: caseId,
                    }).unwrap()
                    setIsDeleteDialogOpen(false)
                    dispatch(clearImageSelection(caseId))
                    toast.success(translate('imageDeleted'))
                    refetch()
                  } catch (err) {
                    toast.error(translate('imageDeleteFailed'))
                  }
                }
              }}
              textTransform="uppercase"
              loading={isDeletingImage}
              disabled={isDeletingImage}
            />
          </Box>
        }
      />

      {isMoveDialog && (
        <MoveImageDialog
          open={isMoveDialog}
          onClose={() => setIsMoveDialog(false)}
          caseId={caseId}
          refetchRecipe={refetch}
        />
      )}

      {isStartTaskMenu && (
        <StartTaskModal
          open={isStartTaskMenu}
          onClose={() => setIsStartTaskMenu(false)}
          imageId={selected}
          caseId={caseId}
          imageTypes={selectedImageTypes}
        />
      )}
    </>
  )
}

export default MultipleImageActions
